import { useState, useRef, useEffect, useMemo, useCallback } from "react";

export function SkuCombobox({ products, value, onChange, disabled, index }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = products.find((p) => p.id === value);

  const reposition = useCallback(() => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Keep the fixed-position dropdown aligned while the page/form scrolls or resizes.
  useEffect(() => {
    if (!open) return;
    reposition();
    const onMove = () => reposition();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, reposition]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? products.filter((p) =>
          p.sku_code.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
      : products;
    return list.slice(0, 50);
  }, [products, query]);

  const pick = (p) => { onChange(p.id); setOpen(false); setQuery(""); };
  const onKeyDown = (e) => {
    if (!open) { if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); setQuery(""); reposition(); e.preventDefault(); } return; }
    if (e.key === "ArrowDown") { setHi((h) => Math.min(h + 1, results.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHi((h) => Math.max(h - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter") { if (results[hi]) pick(results[hi]); e.preventDefault(); }
    else if (e.key === "Escape") { setOpen(false); setQuery(""); }
  };
  useEffect(() => { setHi(0); }, [query, open]);
  useEffect(() => {
    if (!open) return;
    document.querySelector(`[data-testid="item-sku-options-${index}"] .combobox-option.highlight`)?.scrollIntoView({ block: "nearest" });
  }, [hi, open, index]);

  const label = selected ? `${selected.sku_code} — ${selected.description || "tanpa deskripsi"}` : "";

  return (
    <div className="combobox" ref={wrapRef}>
      <input
        ref={inputRef}
        className="field-input"
        placeholder="Ketik SKU / deskripsi…"
        value={open ? query : label}
        disabled={disabled}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(""); reposition(); } }}
        onClick={() => { if (!disabled && !open) { setOpen(true); setQuery(""); reposition(); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        data-testid={`item-sku-search-${index}`}
      />
      {open && !disabled && coords && (
        <div
          className="combobox-list combobox-list--fixed"
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 3000 }}
          data-testid={`item-sku-options-${index}`}
        >
          {results.length === 0 && <div className="combobox-empty">Tidak ada SKU cocok.</div>}
          {results.map((p, i) => (
            <button
              type="button"
              key={p.id}
              className={`combobox-option${p.id === value ? " active" : ""}${i === hi ? " highlight" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(p)}
              data-testid={`item-sku-option-${index}-${p.sku_code}`}
            >
              <span className="mono">{p.sku_code}</span>
              <span className="combobox-desc">{p.description || "tanpa deskripsi"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
